import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  Bell,
  Mail,
  MessageSquare,
  Phone,
  Save,
  RefreshCw,
  Settings
} from 'lucide-react';
import toast from 'react-hot-toast';

import { apiHelper } from '@services/api';
import Button from '@components/common/Button';
import Loading from '@components/common/Loading';
import { Badge } from '@components/common/StatusBadge';

const sectionCard = 'bg-white rounded-2xl shadow-xl border border-gray-100 p-6';

const NotificationSettings = () => {
  const [settings, setSettings] = useState({});
  const queryClient = useQueryClient();

  // Fetch notification settings
  const { data: notificationSettings, isLoading } = useQuery(
    ['notification-settings'],
    async () => {
      const response = await apiHelper.get('/settings/notifications');
      return response.data;
    },
    {
      onSuccess: (data) => {
        setSettings(data || {});
      },
      staleTime: 30000
    }
  );

  // Update settings mutation
  const updateSettingsMutation = useMutation(
    async (updatedSettings) => {
      const response = await apiHelper.put('/settings/notifications', updatedSettings);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['notification-settings']);
        toast.success('Notification settings updated successfully');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update settings');
      }
    }
  );

  // Test notification mutation
  const testNotificationMutation = useMutation(
    async (type) => {
      const response = await apiHelper.post('/settings/notifications/test', { type });
      return response.data;
    },
    {
      onSuccess: (_, type) => {
        toast.success(`Test ${type} notification sent successfully`);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to send test notification');
      }
    }
  );

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleNestedSettingChange = (parent, key, value) => {
    setSettings(prev => ({ ...prev, [parent]: { ...(prev[parent] || {}), [key]: value } }));
  };

  const handleSave = () => {
    updateSettingsMutation.mutate(settings);
  };

  const handleReset = () => {
    if (notificationSettings) {
      setSettings(notificationSettings);
      toast.success('Settings reset to saved values');
    }
  };

  const handleTestNotification = (type) => {
    testNotificationMutation.mutate(type);
  };

  if (isLoading) {
    return (
      <div className="min-h-[200px] flex items-center justify-center">
        <Loading size="lg" text="Loading notification settings..." />
      </div>
    );
  }

  return (
    <motion.div
      className="space-y-6"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.28 }}
    >
      {/* Email Settings */}
      <section className={sectionCard}>
        <div className="flex items-center gap-3 mb-6">
          <Mail className="w-5 h-5 text-primary-600" />
          <h2 className="text-lg font-semibold text-gray-900">Email Settings</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">SMTP Server</label>
            <input
              type="text"
              value={settings.smtp_host || ''}
              onChange={(e) => handleSettingChange('smtp_host', e.target.value)}
              className="w-full px-3 py-2 border rounded-lg border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="smtp.gmail.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">SMTP Port</label>
            <input
              type="number"
              value={settings.smtp_port ?? 587}
              onChange={(e) => handleSettingChange('smtp_port', parseInt(e.target.value || '0'))}
              className="w-full px-3 py-2 border rounded-lg border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">SMTP Username</label>
            <input
              type="email"
              value={settings.smtp_username || ''}
              onChange={(e) => handleSettingChange('smtp_username', e.target.value)}
              className="w-full px-3 py-2 border rounded-lg border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="noreply@rdxlogistics.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">SMTP Password</label>
            <input
              type="password"
              value={settings.smtp_password || ''}
              onChange={(e) => handleSettingChange('smtp_password', e.target.value)}
              className="w-full px-3 py-2 border rounded-lg border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="••••••••"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">From Name</label>
            <input
              type="text"
              value={settings.email_from_name ?? 'RoadExpress Freight'}
              onChange={(e) => handleSettingChange('email_from_name', e.target.value)}
              className="w-full px-3 py-2 border rounded-lg border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">From Email</label>
            <input
              type="email"
              value={settings.email_from_address ?? 'noreply@rdxlogistics.com'}
              onChange={(e) => handleSettingChange('email_from_address', e.target.value)}
              className="w-full px-3 py-2 border rounded-lg border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div className="md:col-span-2 flex flex-wrap gap-4 items-center">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={!!settings.smtp_use_tls}
                onChange={(e) => handleSettingChange('smtp_use_tls', e.target.checked)}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm font-medium text-gray-700">Use TLS</span>
            </label>

            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={settings.email_enabled ?? true}
                onChange={(e) => handleSettingChange('email_enabled', e.target.checked)}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm font-medium text-gray-700">Enable Email Notifications</span>
            </label>

            <div className="ml-auto">
              <Button
                variant="outline"
                leftIcon={<Mail className="w-4 h-4" />}
                onClick={() => handleTestNotification('email')}
                loading={testNotificationMutation.isLoading}
                disabled={!settings.email_enabled}
              >
                                Send Test Email
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Notification Types */}
      <section className={sectionCard}>
        <div className="flex items-center gap-3 mb-6">
          <Bell className="w-5 h-5 text-primary-600" />
          <h2 className="text-lg font-semibold text-gray-900">Notification Types</h2>
        </div>

        <div className="space-y-6">
          {/* Ticket Notifications */}
          <div>
            <h3 className="text-base font-medium text-gray-900 mb-4">Ticket Notifications</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { key: 'ticket_created', label: 'Ticket Created', description: 'When a new ticket is created' },
                { key: 'ticket_assigned', label: 'Ticket Assigned', description: 'When a ticket is assigned to a user' },
                { key: 'ticket_updated', label: 'Ticket Updated', description: 'When ticket details are updated' },
                { key: 'ticket_commented', label: 'New Comment', description: 'When a comment is added' },
                { key: 'ticket_status_changed', label: 'Status Changed', description: 'When ticket status changes' },
                { key: 'ticket_closed', label: 'Ticket Closed', description: 'When a ticket is closed' }
              ].map(n => (
                <div key={n.key} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <input
                    type="checkbox"
                    checked={settings.notifications?.[n.key] ?? true}
                    onChange={(e) => handleNestedSettingChange('notifications', n.key, e.target.checked)}
                    className="mt-1 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <div className="flex-1">
                    <label className="text-sm font-medium text-gray-900">{n.label}</label>
                    <p className="text-xs text-gray-600">{n.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* SLA Notifications */}
          <div>
            <h3 className="text-base font-medium text-gray-900 mb-4">SLA Notifications</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { key: 'sla_warning', label: 'SLA Warning', description: 'When SLA breach is approaching' },
                { key: 'sla_breach', label: 'SLA Breach', description: 'When SLA is breached' },
                { key: 'escalation', label: 'Escalation', description: 'When a ticket is escalated' }
              ].map(n => (
                <div key={n.key} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <input
                    type="checkbox"
                    checked={settings.notifications?.[n.key] ?? true}
                    onChange={(e) => handleNestedSettingChange('notifications', n.key, e.target.checked)}
                    className="mt-1 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <div className="flex-1">
                    <label className="text-sm font-medium text-gray-900">{n.label}</label>
                    <p className="text-xs text-gray-600">{n.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* System Notifications */}
          <div>
            <h3 className="text-base font-medium text-gray-900 mb-4">System Notifications</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { key: 'user_created', label: 'User Created', description: 'When a new user is added' },
                { key: 'user_login', label: 'User Login', description: 'When a user logs in (admin only)' },
                { key: 'system_maintenance', label: 'System Maintenance', description: 'Maintenance notifications' },
                { key: 'backup_completed', label: 'Backup Completed', description: 'When system backup completes' }
              ].map(n => (
                <div key={n.key} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <input
                    type="checkbox"
                    checked={settings.notifications?.[n.key] ?? false}
                    onChange={(e) => handleNestedSettingChange('notifications', n.key, e.target.checked)}
                    className="mt-1 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <div className="flex-1">
                    <label className="text-sm font-medium text-gray-900">{n.label}</label>
                    <p className="text-xs text-gray-600">{n.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Preferences */}
      <section className={sectionCard}>
        <div className="flex items-center gap-3 mb-6">
          <Settings className="w-5 h-5 text-primary-600" />
          <h2 className="text-lg font-semibold text-gray-900">Notification Preferences</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Notification Frequency</label>
            <select
              value={settings.notification_frequency || 'immediate'}
              onChange={(e) => handleSettingChange('notification_frequency', e.target.value)}
              className="w-full px-3 py-2 border rounded-lg border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="immediate">Immediate</option>
              <option value="hourly">Hourly Digest</option>
              <option value="daily">Daily Digest</option>
              <option value="weekly">Weekly Digest</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Quiet Hours Start</label>
            <input
              type="time"
              value={settings.quiet_hours_start || '22:00'}
              onChange={(e) => handleSettingChange('quiet_hours_start', e.target.value)}
              className="w-full px-3 py-2 border rounded-lg border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Quiet Hours End</label>
            <input
              type="time"
              value={settings.quiet_hours_end || '08:00'}
              onChange={(e) => handleSettingChange('quiet_hours_end', e.target.value)}
              className="w-full px-3 py-2 border rounded-lg border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Max Notifications Per Hour</label>
            <input
              type="number"
              value={settings.max_notifications_per_hour ?? 10}
              onChange={(e) => handleSettingChange('max_notifications_per_hour', parseInt(e.target.value || '0'))}
              min={1}
              max={100}
              className="w-full px-3 py-2 border rounded-lg border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div className="md:col-span-2 space-y-3">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={!!settings.enable_quiet_hours}
                onChange={(e) => handleSettingChange('enable_quiet_hours', e.target.checked)}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm font-medium text-gray-700">Enable quiet hours</span>
            </label>

            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={settings.enable_weekends ?? true}
                onChange={(e) => handleSettingChange('enable_weekends', e.target.checked)}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm font-medium text-gray-700">Send notifications on weekends</span>
            </label>

            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={!!settings.enable_mobile_push}
                onChange={(e) => handleSettingChange('enable_mobile_push', e.target.checked)}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm font-medium text-gray-700">Enable mobile push notifications</span>
            </label>
          </div>
        </div>
      </section>

      {/* Email Templates */}
      <section className={sectionCard}>
        <div className="flex items-center gap-3 mb-6">
          <MessageSquare className="w-5 h-5 text-primary-600" />
          <h2 className="text-lg font-semibold text-gray-900">Email Templates</h2>
        </div>

        <p className="text-sm text-gray-600 mb-4">
                    Customize email templates for different notification types. Use variables like <code className="bg-gray-100 px-1 rounded">{'{user_name}'}</code>, <code className="bg-gray-100 px-1 rounded">{'{ticket_number}'}</code>, etc.
        </p>

        <div className="grid grid-cols-1 gap-4">
          {[
            { key: 'ticket_created_template', label: 'Ticket Created Template' },
            { key: 'ticket_assigned_template', label: 'Ticket Assigned Template' },
            { key: 'ticket_closed_template', label: 'Ticket Closed Template' },
            { key: 'sla_breach_template', label: 'SLA Breach Template' }
          ].map(t => (
            <div key={t.key}>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t.label}</label>
              <textarea
                value={settings.email_templates?.[t.key] ?? ''}
                onChange={(e) => handleNestedSettingChange('email_templates', t.key, e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border rounded-lg border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-500 font-mono text-sm"
                placeholder={`Enter ${t.label.toLowerCase()}...`}
              />
            </div>
          ))}
        </div>

        <div className="bg-blue-50 rounded-lg p-4 mt-4">
          <h4 className="text-sm font-medium text-blue-900 mb-2">Available Variables</h4>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs text-blue-800">
            <code>{'{user_name}'}</code>
            <code>{'{ticket_number}'}</code>
            <code>{'{ticket_subject}'}</code>
            <code>{'{ticket_status}'}</code>
            <code>{'{ticket_priority}'}</code>
            <code>{'{assigned_to}'}</code>
            <code>{'{created_date}'}</code>
            <code>{'{due_date}'}</code>
            <code>{'{company_name}'}</code>
          </div>
        </div>
      </section>

      {/* Integrations */}
      <section className={sectionCard}>
        <div className="flex items-center gap-3 mb-6">
          <Phone className="w-5 h-5 text-primary-600" />
          <h2 className="text-lg font-semibold text-gray-900">Third-party Integrations</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Slack */}
          <div className="p-4 border border-gray-100 rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-medium text-gray-900">Slack</h3>
              <Badge variant={settings.slack_enabled ? 'success' : 'default'} size="sm">
                {settings.slack_enabled ? 'Connected' : 'Disabled'}
              </Badge>
            </div>
            <p className="text-sm text-gray-600 mb-3">Send notifications to Slack channels</p>

            <div className="space-y-3">
              <input
                type="text"
                placeholder="Slack Webhook URL"
                value={settings.slack_webhook_url || ''}
                onChange={(e) => handleSettingChange('slack_webhook_url', e.target.value)}
                className="w-full px-3 py-2 text-sm border rounded-lg border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={!!settings.slack_enabled}
                  onChange={(e) => handleSettingChange('slack_enabled', e.target.checked)}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">Enable Slack notifications</span>
              </label>
            </div>
          </div>

          {/* WhatsApp */}
          <div className="p-4 border border-gray-100 rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-medium text-gray-900">WhatsApp</h3>
              <Badge variant={settings.whatsapp_enabled ? 'success' : 'default'} size="sm">
                {settings.whatsapp_enabled ? 'Connected' : 'Disabled'}
              </Badge>
            </div>
            <p className="text-sm text-gray-600 mb-3">Send notifications via WhatsApp Business API</p>

            <div className="space-y-3">
              <input
                type="text"
                placeholder="WhatsApp API Key"
                value={settings.whatsapp_api_key || ''}
                onChange={(e) => handleSettingChange('whatsapp_api_key', e.target.value)}
                className="w-full px-3 py-2 text-sm border rounded-lg border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={!!settings.whatsapp_enabled}
                  onChange={(e) => handleSettingChange('whatsapp_enabled', e.target.checked)}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">Enable WhatsApp notifications</span>
              </label>
            </div>
          </div>
        </div>
      </section>

      {/* Save Actions */}
      <div className="flex items-center justify-between bg-gray-50 rounded-2xl p-4">
        <div className="text-sm text-gray-600">Changes will be applied immediately after saving.</div>

        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            leftIcon={<RefreshCw className="w-4 h-4" />}
            onClick={handleReset}
          >
                        Reset
          </Button>

          <Button
            variant="primary"
            leftIcon={<Save className="w-4 h-4" />}
            onClick={handleSave}
            loading={updateSettingsMutation.isLoading}
          >
                        Save Settings
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

export default NotificationSettings;
